import type { Dispatch, SetStateAction } from 'react'
import React, { memo, useCallback, useMemo, useRef, useState } from 'react'

import { AuthType } from 'constants/constants'

import { Box, Button, CircularProgress, Grid } from '@mui/material'
import { useAuthContext } from 'AuthProvider'
import { buildRecDateToNumberIndex, resolveChartClickedData } from 'components/parts/Dashboard/chartClickUtils'
import { initialChartState, type InitialChartState } from 'pages/Dashboard/Dashboard'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { mediumFontSize } from 'style'

import type { MonthReport, Stock } from 'api-client'
import type { MouseHandlerDataParam } from 'recharts'
import type { CurveType } from 'recharts/types/shape/Curve'

/** Recharts 3+: height only accepts number or string of the form `${number}%`. */
function toResponsiveContainerHeight(height: string | number): number | `${number}%` {
  if (typeof height === 'number') return height
  const s = String(height).trim()
  if (/^-?\d+(\.\d+)?%$/.test(s)) return s as `${number}%`
  const n = Number(s)
  if (!Number.isNaN(n)) return n
  return 400
}

export interface StackedBarItem {
  name: string
  dataKey: string
  fill: string
  yAxisId?: string
}

export interface OverlayLineItem {
  name: string
  dataKey: string
  stroke: string
  type?: CurveType
  yAxisId?: string
  dashedLine?: boolean
  dot?: boolean
  /** 線を描画せず、dotだけ表示したい場合にfalse */
  showLine?: boolean
  hide?: boolean
}

export interface StackedBarCart {
  height: string | number
  xAxis: string
  yAxisLabel?: string
  yAxisRight?: string
  yAxisRightLabel?: string
  bars: StackedBarItem[]
  lines?: OverlayLineItem[]
}

interface StackedBarChartProps {
  list: Stock[] | MonthReport[]
  chartDetail: StackedBarCart
  chartState: InitialChartState
  setChartState: Dispatch<SetStateAction<InitialChartState>>
  chartZoom: (
    chartState: InitialChartState,
    setChartState: Dispatch<SetStateAction<InitialChartState>>,
    data: Stock[] | MonthReport[],
    chartDetail: StackedBarCart
  ) => void
  chartZoomOut: (setChartState: Dispatch<SetStateAction<InitialChartState>>) => void
  recDateFormatter: (trRecDateToNumber: number) => string
  isLoading: boolean
  onPlotSelectDate?: (selected: Stock | MonthReport) => void
  todayLineX?: number | null
  data: Stock[] | MonthReport[]
  /** 左右のY軸の目盛りラベルをフォーマットする関数（例: 小数桁で表示） */
  yAxisTickFormatter?: (value: number) => string
}

const legendFormatter = (value: string) => <span style={{ color: 'black' }}>{value}</span>

function StackedBarChart({
  list,
  chartDetail,
  chartState,
  setChartState,
  chartZoom,
  chartZoomOut,
  recDateFormatter,
  isLoading,
  onPlotSelectDate,
  todayLineX,
  data,
  yAxisTickFormatter,
}: StackedBarChartProps) {
  const { loginUserInfo } = useAuthContext()
  const { left, right, top, bottom, top2, bottom2 } = chartState
  const leftYAxisDomain = useMemo(
    (): [number | string | ((dataMin: number) => number), number | string | ((dataMax: number) => number)] => [
      bottom === 'dataMin' ? (dataMin: number) => Math.floor(dataMin) : bottom,
      top === 'dataMax' ? (dataMax: number) => Math.ceil(dataMax) : top,
    ],
    [bottom, top]
  )
  const rightYAxisDomain = useMemo(
    (): [number | string | ((dataMin: number) => number), number | string | ((dataMax: number) => number)] => [
      bottom2 === 'dataMin' ? (dataMin: number) => Math.floor(dataMin) : bottom2,
      top2 === 'dataMax' ? (dataMax: number) => Math.ceil(dataMax) : top2,
    ],
    [bottom2, top2]
  )
  const [refAreaLeft, setRefAreaLeft] = useState<string | number | undefined>('')
  const [refAreaRight, setRefAreaRight] = useState<string | number | undefined>('')
  const suppressPlotClickRef = useRef(false)
  const chartStateRef = useRef(chartState)
  chartStateRef.current = chartState
  const byRecDate = useMemo(() => buildRecDateToNumberIndex(list), [list])

  const plotClickDisabled =
    loginUserInfo?.authType === AuthType.ReadOnly ||
    Boolean(list[0]?.companyId && loginUserInfo?.companyId !== list[0]?.companyId)

  const handlePlotClick = useCallback(
    (state: MouseHandlerDataParam) => {
      if (suppressPlotClickRef.current) {
        suppressPlotClickRef.current = false
        return
      }
      const clickedData = resolveChartClickedData(list, byRecDate, state)
      if (clickedData?.recDate) {
        onPlotSelectDate?.(clickedData)
      }
    },
    [list, byRecDate, onPlotSelectDate]
  )

  const handleZoomOut = useCallback(() => {
    chartZoomOut(setChartState)
  }, [chartZoomOut, setChartState])

  const handleMouseDown = useCallback((e: MouseHandlerDataParam | null) => {
    if (e === null) return
    if (e.activeLabel != null) {
      suppressPlotClickRef.current = false
      setRefAreaLeft(e.activeLabel)
      setRefAreaRight(e.activeLabel)
    }
  }, [])

  const handleMouseMove = useCallback((e: MouseHandlerDataParam | null) => {
    if (refAreaLeft && e !== null && e.activeLabel != null) {
      setRefAreaRight(e.activeLabel)
      if (e.activeLabel !== refAreaLeft) {
        suppressPlotClickRef.current = true
      }
    }
  }, [refAreaLeft])

  const handleMouseUp = useCallback(() => {
    chartZoom(
      { ...chartStateRef.current, refAreaLeft, refAreaRight },
      setChartState,
      data,
      chartDetail
    )
    setRefAreaLeft('')
    setRefAreaRight('')
  }, [chartZoom, chartDetail, data, refAreaLeft, refAreaRight, setChartState])

  const yAxisTickFormatterLeft = useMemo(
    () => (yAxisTickFormatter ? (value: number) => yAxisTickFormatter(Number(value)) : undefined),
    [yAxisTickFormatter]
  )

  const lineElements = useMemo(
    () =>
      chartDetail.lines?.map((line) => {
        let dotProp: boolean | { r: number; stroke: string; fill: string; strokeWidth: number } = line.dot ?? true
        if (line.hide || line.dot === false) {
          dotProp = false
        } else if (dotProp === true) {
          dotProp = {
            r: 3,
            stroke: line.stroke,
            fill: line.stroke,
            strokeWidth: 1,
          }
        }

        let lineStrokeWidth = 3
        if (line.hide || line.showLine === false) {
          lineStrokeWidth = 0
        }

        return (
          <Line
            key={line.dataKey}
            name={line.name}
            type={line.type ?? 'monotone'}
            dataKey={line.dataKey}
            stroke={line.hide ? 'transparent' : line.stroke}
            yAxisId={line.yAxisId || '1'}
            dot={dotProp}
            strokeWidth={lineStrokeWidth}
            strokeDasharray={line.dashedLine ? '3 4' : undefined}
            style={{ visibility: line.hide ? 'hidden' : 'visible' }}
            isAnimationActive={false}
            legendType={line.hide ? 'none' : undefined}
          />
        )
      }),
    [chartDetail.lines]
  )

  return (
    <Box
      sx={{
        width: '100%',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        msUserSelect: 'none',
      }}
      onMouseDownCapture={(e) => {
        e.preventDefault()
      }}
      onDragStartCapture={(e) => {
        e.preventDefault()
      }}
    >
      {isLoading ? (
        <Grid container alignItems="center" justifyContent="center" direction="column">
          <Grid component="div">
            <CircularProgress color="inherit" size={70} />
          </Grid>
        </Grid>
      ) : (
        <>
          <Button
            type="button"
            variant="contained"
            color="info"
            onClick={handleZoomOut}
            disabled={left === initialChartState.left && right === initialChartState.right}
            sx={{ marginBottom: 1 }}
          >
            グラフを元に戻す
          </Button>
          <ResponsiveContainer width="100%" height={toResponsiveContainerHeight(chartDetail.height)}>
            <ComposedChart<Stock | MonthReport>
              data={list}
              onClick={plotClickDisabled ? undefined : handlePlotClick}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
            >
              <CartesianGrid stroke="#eee" strokeDasharray="3 3" />
              <XAxis
                allowDataOverflow
                dataKey={chartDetail.xAxis}
                domain={[left as string, right as string]}
                type="number"
                tickFormatter={recDateFormatter}
              />
              <YAxis
                yAxisId="1"
                allowDataOverflow
                allowDecimals={false}
                domain={leftYAxisDomain}
                type="number"
                tickFormatter={yAxisTickFormatterLeft}
                label={{
                  value: chartDetail.yAxisLabel ?? '',
                  angle: 0,
                  dy: 20,
                  dx: -10,
                  position: 'insideTop',
                  fill: 'black',
                }}
              />
              {chartDetail.yAxisRight && (
                <YAxis
                  orientation="right"
                  yAxisId={chartDetail.yAxisRight}
                  allowDataOverflow
                  allowDecimals={false}
                  domain={rightYAxisDomain}
                  type="number"
                  tickFormatter={yAxisTickFormatterLeft}
                  label={{
                    value: chartDetail.yAxisRightLabel ?? '',
                    angle: 0,
                    dy: 20,
                    dx: 10,
                    position: 'insideTop',
                    fill: 'black',
                  }}
                />
              )}
              <Tooltip
                labelFormatter={(value) => recDateFormatter(Number(value))}
                itemStyle={{ color: 'black' }}
              />
              <Legend
                iconSize={20}
                wrapperStyle={{ fontSize: mediumFontSize }}
                formatter={legendFormatter}
              />
              {chartDetail.bars.map((bar) => (
                <Bar
                  key={bar.dataKey}
                  name={bar.name}
                  dataKey={bar.dataKey}
                  fill={bar.fill}
                  yAxisId={bar.yAxisId || '1'}
                  stackId="stack"
                  isAnimationActive={false}
                />
              ))}
              {lineElements}
              {refAreaLeft && refAreaRight ? (
                <ReferenceArea yAxisId="1" x1={refAreaLeft} x2={refAreaRight} strokeOpacity={0.3} />
              ) : null}
              {todayLineX != null ? (
                <ReferenceLine
                  yAxisId="1"
                  x={todayLineX}
                  stroke="#ff0000"
                  strokeDasharray="3 3"
                  label={{ value: '本日', position: 'top', fill: '#ff0000' }}
                />
              ) : null}
            </ComposedChart>
          </ResponsiveContainer>
        </>
      )}
    </Box>
  )
}

export default memo(StackedBarChart)
